import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useTranslation } from 'next-i18next';
import {
  TimelineScrollContainer,
  TimelineInner,
  DayHeaderCell,
  JobRow,
  JobLabel,
  JobDot,
  JobName,
  BarCell,
  Bar,
  EmptyState,
  MonthHeaderCell,
  TooltipContent,
  TooltipDate,
  TooltipJobItem,
  TooltipCost,
} from './styled';
import { COST_PER_DAY } from './constants';
import { formatDate } from './utils';
import type { DayInfo } from './types';

interface ConsumptionTimelineProps {
  days: DayInfo[];
  visibleJobIds: string[];
  jobColorMap: Record<string, string>;
  jobTitleMap: Record<string, string>;
  logSet: Set<string>;
  today: Date;
}

const ConsumptionTimeline: React.FC<ConsumptionTimelineProps> = ({
  days,
  visibleJobIds,
  jobColorMap,
  jobTitleMap,
  logSet,
  today,
}) => {
  const { t } = useTranslation('payment-and-invoices');
  const [hoveredDayIdx, setHoveredDayIdx] = React.useState<number | null>(null);
  const dayHeaderRefs = React.useRef<Map<number, HTMLElement>>(new Map());

  const daysAbbr = t('days_abbr', { returnObjects: true }) as string[];
  const monthsAbbr = t('months_abbr', { returnObjects: true }) as string[];
  const monthsFull = t('months_full', { returnObjects: true }) as string[];

  const colWidth = days.length > 28 ? 20 : days.length > 14 ? 24 : days.length > 7 ? 40 : 80;
  const isCompact = days.length > 14;
  const todayStr = formatDate(today);

  // Dismiss tooltip on any scroll (capture phase catches inner scroll containers)
  React.useEffect(() => {
    const handler = () => setHoveredDayIdx(null);
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, []);

  const monthGroups = React.useMemo(() => {
    const groups: { label: string; span: number }[] = [];
    let currentKey = '';
    days.forEach((d) => {
      const key = `${d.date.getFullYear()}-${d.date.getMonth()}`;
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ label: monthsAbbr[d.date.getMonth()], span: 1 });
      } else {
        groups[groups.length - 1].span++;
      }
    });
    return groups;
  }, [days, monthsAbbr]);

  const showMonthRow = monthGroups.length > 1;

  // Tooltip data for the hovered day column
  const hoveredDayData = React.useMemo(() => {
    if (hoveredDayIdx === null) return null;
    const d = days[hoveredDayIdx];
    if (!d || d.date > today) return null;

    const dayJobs = visibleJobIds
      .filter((jobId) => logSet.has(`${jobId}|${d.str}`))
      .map((jobId) => ({
        jobId,
        title: jobTitleMap[jobId],
        color: jobColorMap[jobId],
      }));

    if (dayJobs.length === 0) return null;

    return { dayJobs, date: d.date, totalCost: dayJobs.length * COST_PER_DAY };
  }, [hoveredDayIdx, days, today, visibleJobIds, logSet, jobTitleMap, jobColorMap]);

  // Position tooltip above the hovered DayHeaderCell
  const [tooltipPos, setTooltipPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    if (hoveredDayIdx === null || !hoveredDayData) {
      setTooltipPos(null);
      return;
    }
    const el = dayHeaderRefs.current.get(hoveredDayIdx);
    if (!el) {
      setTooltipPos(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setTooltipPos({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, [hoveredDayIdx, hoveredDayData]);

  return (
    <TimelineScrollContainer>
      <TimelineInner style={{ minWidth: days.length * colWidth + 140 }}>
        {/* Month header row */}
        {showMonthRow && (
          <div className="flex" style={{ marginLeft: 140 }}>
            {monthGroups.map((g, i) => (
              <MonthHeaderCell key={i} style={{ width: g.span * colWidth }}>
                {g.label}
              </MonthHeaderCell>
            ))}
          </div>
        )}

        {/* Date header row */}
        <div className="flex" style={{ marginLeft: 140 }}>
          {days.map((d, i) => {
            const isToday = d.str === todayStr;
            const isFuture = d.date > today;

            return (
              <DayHeaderCell
                key={i}
                ref={(el: HTMLDivElement | null) => {
                  if (el) dayHeaderRefs.current.set(i, el);
                  else dayHeaderRefs.current.delete(i);
                }}
                isToday={isToday}
                isFuture={isFuture}
                style={{ width: colWidth }}
                onMouseEnter={() => !isFuture && setHoveredDayIdx(i)}
                onMouseLeave={() => setHoveredDayIdx(null)}
              >
                {isCompact ? (
                  <>{d.date.getDate()}</>
                ) : (
                  <>
                    <div>{daysAbbr[d.date.getDay()]}</div>
                    <div>{d.date.getDate()}</div>
                  </>
                )}
              </DayHeaderCell>
            );
          })}
        </div>

        {/* Job rows */}
        {visibleJobIds.map((jobId, index) => {
          const color = jobColorMap[jobId];
          const title = jobTitleMap[jobId];
          const isLastRow = index === visibleJobIds.length - 1;
          const isFirstRow = index === 0;
          return (
            <JobRow key={jobId} $isLast={isLastRow} $isFirst={isFirstRow}>
              <JobLabel>
                <JobDot color={color} />
                <JobName title={title}>{title}</JobName>
              </JobLabel>
              {days.map((d, i) => {
                const active = logSet.has(`${jobId}|${d.str}`);
                const isFuture = d.date > today;
                const prevActive = i > 0 && logSet.has(`${jobId}|${days[i - 1].str}`);
                const nextActive = i < days.length - 1 && logSet.has(`${jobId}|${days[i + 1].str}`);
                const isHovered = hoveredDayIdx === i;

                return (
                  <BarCell
                    key={i}
                    style={{ width: colWidth }}
                    onMouseEnter={() => !isFuture && setHoveredDayIdx(i)}
                    onMouseLeave={() => setHoveredDayIdx(null)}
                  >
                    {active && !isFuture && (
                      <Bar color={color} isHovered={isHovered} roundLeft={!prevActive} roundRight={!nextActive} />
                    )}
                  </BarCell>
                );
              })}
            </JobRow>
          );
        })}

        {visibleJobIds.length === 0 && <EmptyState>{t('no_positions_in_period')}</EmptyState>}
      </TimelineInner>

      {/* Portal tooltip — rendered in document.body to avoid clipping */}
      {hoveredDayData &&
        tooltipPos &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: tooltipPos.top,
              left: tooltipPos.left,
              transform: 'translate(-50%, -100%)',
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                backgroundColor: '#171717',
                color: '#fff',
                fontSize: '0.75rem',
                padding: '0.5rem',
                borderRadius: '0.5rem',
                position: 'relative',
              }}
            >
              <TooltipContent>
                <TooltipDate>
                  {hoveredDayData.date.getDate()} {monthsFull[hoveredDayData.date.getMonth()]}
                </TooltipDate>
                {hoveredDayData.dayJobs.map((job) => (
                  <TooltipJobItem key={job.jobId}>
                    <JobDot color={job.color} className="opacity-90" />
                    <span className="opacity-80">{job.title}</span>
                  </TooltipJobItem>
                ))}
                <TooltipCost>&euro;{hoveredDayData.totalCost.toFixed(2)}</TooltipCost>
              </TooltipContent>
              <div
                style={{
                  position: 'absolute',
                  bottom: -4,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: '5px solid #171717',
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </TimelineScrollContainer>
  );
};

export default ConsumptionTimeline;
